'use client';

import { FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Clock, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import styles from './WorkScheduleModal.module.scss';
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type DayKey = typeof DAY_KEYS[number];
const JS_DAY_TO_KEY: Record<number, DayKey> = {
  0: 'sun',
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
  6: 'sat'
};
export interface WorkScheduleModalProps {
  open: boolean;
  onClose: () => void;
  workingHours?: Record<string, string>;
}
const WorkScheduleModal: FC<WorkScheduleModalProps> = ({
  open,
  onClose,
  workingHours
}) => {
  const t = useTranslations();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);
  if (!open) return null;
  if (typeof document === 'undefined') return null;
  const todayKey = JS_DAY_TO_KEY[new Date().getDay()];
  return createPortal(<div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true" aria-label={t('workspace-details.workSchedule')}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <div className={styles.header}>
                    <div className={styles.title}>
                        <Clock size={20} strokeWidth={2} />
                        <h2>{t('workspace-details.workSchedule')}</h2>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
                        <X size={20} />
                    </button>
                </div>

                <ul className={styles.list}>
                    {DAY_KEYS.map(key => {
          const hours = workingHours?.[key];
          const isToday = key === todayKey;
          return <li key={key} className={`${styles.row} ${isToday ? styles.today : ''}`}>
                                <span className={styles.day}>{t(key)}</span>
                                <span className={styles.hours}>
                                    {hours || t('workspace-details.off')}
                                </span>
                            </li>;
        })}
                </ul>
            </div>
        </div>, document.body);
};
export default WorkScheduleModal;